import { ChangeDetectorRef, Component, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { take } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AppService } from '../../app.service';
import { AccountService } from '../../services/AccountService';
import { MoovStep } from '../../models/moovStep';
import { PremierServiceProduct } from '../../models/premierServiceProduct';
import { MatSelectionListChange } from '@angular/material/list';
import { AbstractParameters, AbstractParameter, Preset } from '../../models/preset';
import { uniquePresetName } from '../../theme/utils/app-validators';

@Component({ standalone:false,
  selector: 'app-edit-preset-dialog',
  templateUrl: './edit-preset-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./edit-preset-dialog.component.scss']
})
export class EditPresetDialogComponent {
  public title: string = "Edit preset"
  public cancelButtonText = "CANCEL";
  public submitButtonText = "SAVE CHANGES";
  public deleteButtonText = "DELETE";
  public moovStepArray:MoovStep[];
  public namesTaken:string[]=[];
  public preset:Preset;
  public otherPresets:Preset[]=[];
  public abstractParameters:AbstractParameter[]=[];

  public presetForm:FormGroup;
  public busy=true;

  private pspArray:PremierServiceProduct[]=[];
  private sortBySeqNo:boolean=false;

  constructor(@Inject(MAT_DIALOG_DATA) private data: any, private dialogRef: MatDialogRef<EditPresetDialogComponent>, private accService:AccountService, private cdRef:ChangeDetectorRef) {
    this.moovStepArray = this.data?.moovStepArray;
    this.preset=data.preset;
    if(data.otherPresets){
      this.otherPresets=data.otherPresets;
      this.namesTaken=this.otherPresets.map(p=>p.name);
    } 
    this.presetForm = new FormGroup({
      presetName: new FormControl<string|undefined>(undefined, Validators.compose([uniquePresetName(this.namesTaken), Validators.required, Validators.minLength(3), Validators.maxLength(100)])),
      presets : new FormControl<AbstractParameter|undefined>(undefined, Validators.required)
    });
  }

  ngAfterViewInit(){
     //get premier service products and identify which one is being used in current moovStepArray
    this.accService.getPremierServiceProducts(false).pipe(take(1)).subscribe((pspArray)=>{
        this.pspArray=pspArray;
        this.createAbstractParameterSet();
        if(this.preset) this.populateForm(this.preset);
    });
  }

  private createAbstractParameterSet(){
    this.presetForm.get("presets")!.setValue(undefined);
    this.abstractParameters=[];
    if(this.moovStepArray){
      this.moovStepArray.forEach(ms=>{
        let usedpsp = this.pspArray?.find(psp=>psp.contextType=="moovstepdefinition" && psp.contextId==ms.moovStepDefinition.id);
        if(usedpsp) this.abstractParameters.push(...new AbstractParameters(ms, usedpsp).parameterArray);
        else this.abstractParameters.push(...new AbstractParameters(ms).parameterArray);
      });
      //now we have all abstract parameters => need to remove those used in other presets
      let usedParameters:AbstractParameter[]=[];
      if(this.otherPresets){
        this.otherPresets.forEach(preset=>{
          this.abstractParameters.forEach(ap=>{
            if(preset.containsParameter(ap)) usedParameters.push(ap);
          });
        });
        this.abstractParameters=this.abstractParameters.filter(ap => !usedParameters.includes(ap));
      }
      this.abstractParameters = this.sortBySeqNo? this.abstractParameters.sort((x,y)=>x.moovStepSequenceNo-y.moovStepSequenceNo) : this.abstractParameters.sort((x,y)=>x.label.localeCompare(y.label));
    }
    this.busy=false;
    this.cdRef.detectChanges();
    //now we have a list of all usable parameters used in the moov definition
  }

  onSortChanged($event:any){
    this.sortBySeqNo=$event.checked;
    let tempPreset=new Preset();
    tempPreset.name = this.presetForm.get("presetName")!.value;
    tempPreset.parameters = this.presetForm.get("presets")!.value;
    this.createAbstractParameterSet();
    this.populateForm(tempPreset)
  }

  onSelectionChange(event: MatSelectionListChange) {
    let selectedAbstractParameters = this.presetForm.get("presets")!.value;
    if(selectedAbstractParameters.length === 1){
      //filter source parameters to only contain compatible items to the first seelcted item
      let firstSelected = selectedAbstractParameters[0];
      this.abstractParameters.forEach(ap =>{
          ap.disabled = !AbstractParameter.isCompatible(firstSelected, ap);
      });  
    }
    else if(selectedAbstractParameters.length === 0){
      //enable all parameters again
      this.abstractParameters.forEach(ap => {
        ap.disabled = false;
      });
    }
  }

  private populateForm(preset:Preset){
    this.presetForm.get("presetName")!.setValue(preset.name);
    let preselectedParams:AbstractParameter[]=[];
    preset.parameters?.forEach(p=>{
      let ap = this.abstractParameters.find(ap=>ap.name==p.name && ap.moovStepDefinitionId==p.moovStepDefinitionId && ap.moovStepSequenceNo==p.moovStepSequenceNo);
      if(ap) preselectedParams.push(ap);
    });
    if(preselectedParams.length>0){
      this.presetForm.get("presets")!.setValue(preselectedParams);
      //disable all incompatible ones
      let notPreselectedParams:AbstractParameter[]=this.abstractParameters.filter(ap => preselectedParams.indexOf(ap)==-1);
      notPreselectedParams.forEach(ap =>{
        ap.disabled = !AbstractParameter.isCompatible(preselectedParams[0], ap);
      });  
    }
  }

  get usageTypeArray(){
    return AppService.usageTypeArray;
  }

  get canSubmit(){
    return this.presetForm.valid && this.presetForm.dirty && !this.busy;
  }

  public onSubmitClick(): void {
    let preset = new Preset();
    preset.name = this.presetForm.get("presetName")!.value;
    preset.parameters = this.presetForm.get("presets")!.value;
    this.dialogRef.close({preset:preset, action:this.preset ? 'update' : 'create'});
  }

  public onDeleteClick(): void {
    this.dialogRef.close({preset:this.preset, action:'delete'});
  }

  public onCancelClick(): void {
    this.dialogRef.close(undefined);
  }

}
